/**
 * useMyCommunities — the single source for "communities the current user
 * leads" (is OWNER or ADMIN of), unioning the legacy V1
 * `community_role_assignments` read with the V2 `permission_grants`
 * evaluator during the shadow-mode window.
 *
 * Extracted from MyCommunitiesCardsPage (the cards page now consumes this
 * hook) so the Communities Rail and the page compute membership identically
 * instead of duplicating the union — per the Rails Phase 1 handoff
 * ("Extract that to a shared hook; do not duplicate").
 *
 * NOTE ON SCOPE: this is the owner/admin (Lead-tier) set, the same set the
 * /my-communities page shows — NOT every community the user is a plain
 * member of. The Communities switcher Rail reuses it deliberately.
 *
 * ASYNC: the V2 evaluator (useV2CommunityRoles) populates after an async
 * fetch (post-DEV-932). `myGuids`/`myCommunities` therefore grow once V2
 * resolves; `loading` reflects that. Any consumer memo/effect that reads
 * these MUST list them in its deps (react-hooks/exhaustive-deps is `error`)
 * or it will latch onto the empty first render and never recover.
 */
import { useMemo } from 'react';
import { useApiSecurityData } from '../../api/useApiSecurityData';
import { useV2CommunityRoles } from '../../security/v2/useV2CommunityRoles';
import {
  buildEntityMap,
  buildParentMap,
  type CommunityEntity,
  type CommunityRelationship,
} from './communityIdentity';

/** Roles that qualify a community for the Lead-tier "my communities" set. */
const LEAD_ROLES = new Set(['owner', 'admin']);

export interface UseMyCommunitiesArgs {
  entities?: CommunityEntity[];
  relationships?: CommunityRelationship[];
  currentUserGuid: string;
  /** Seed-shape rows: { user_guid, community_entity_guid, role }. */
  apiCommunityRoles?: Record<string, any>[];
}

export interface UseMyCommunitiesResult {
  /** Upper-cased GUIDs of communities the user owns/admins. */
  myGuids: Set<string>;
  /** Active, non-workshop community entities the user owns/admins. */
  myCommunities: CommunityEntity[];
  /** Upper-cased entity_guid → entity. */
  entityMap: Map<string, CommunityEntity>;
  /** Upper-cased child → parent. */
  parentMap: Map<string, string>;
  /** True while the V2 evaluator is still resolving. */
  loading: boolean;
}

export function useMyCommunities({
  entities,
  relationships,
  currentUserGuid,
  apiCommunityRoles,
}: UseMyCommunitiesArgs): UseMyCommunitiesResult {
  // DEV-971: the V2 evaluator silently drops requires_2fa bundles (Community
  // Admin / Owner per migration 130) unless the user has TOTP enrolled —
  // pass the user's 2FA state through or those communities vanish.
  const { securityUsers } = useApiSecurityData();
  const is2FAEnabled = useMemo(() => {
    const me = String(currentUserGuid || '').toUpperCase();
    if (!me) return false;
    const rec = (securityUsers || []).find(
      (u: any) => String(u.user_guid).toUpperCase() === me,
    );
    return !!rec?.is_two_factor_enabled;
  }, [securityUsers, currentUserGuid]);

  const { capsByCommunity: v2Caps, loading } = useV2CommunityRoles({
    userGuid: currentUserGuid,
    is2FAEnabled,
    entities,
    relationships,
  });

  // V1 read (community_role_assignments owner/admin) unioned with V2
  // (permission_grants → community:admin). Mirrors MyCommunitiesCardsPage.
  const myGuids = useMemo(() => {
    const set = new Set<string>();
    const me = String(currentUserGuid || '').toUpperCase();
    if (!me) return set;

    for (const r of apiCommunityRoles || []) {
      const role = String(r.role || '').toLowerCase();
      if (
        String(r.user_guid || '').toUpperCase() === me &&
        LEAD_ROLES.has(role)
      ) {
        set.add(String(r.community_entity_guid || '').toUpperCase());
      }
    }

    for (const [guid, caps] of v2Caps) {
      if (caps.has('community:admin')) {
        set.add(guid);
      }
    }

    return set;
  }, [apiCommunityRoles, currentUserGuid, v2Caps]);

  const entityMap = useMemo(() => buildEntityMap(entities), [entities]);
  const parentMap = useMemo(() => buildParentMap(relationships), [relationships]);

  // DEV-961: exclude private workshops (personal_owner_user_guid set) and
  // inactive communities from the user-facing navigation.
  const myCommunities = useMemo(() => {
    return (entities || []).filter(
      (c) =>
        myGuids.has(c.entity_guid.toUpperCase()) &&
        c.is_active !== false &&
        !c.personal_owner_user_guid,
    );
  }, [entities, myGuids]);

  return { myGuids, myCommunities, entityMap, parentMap, loading };
}

export default useMyCommunities;
